import { Box, Chip, Skeleton, Stack, Typography } from "@mui/material"

import UserAvatar from "./UserAvatar"
import { relativeTime } from "../utils/format"

/**
 * What happened, most recent first.
 *
 * ── Why a feed and not a log ────────────────────────────────────────────────
 *
 * The audit table is a log: every field change, with old and new values. That
 * is the right shape for answering "who changed this and when" under
 * challenge, and the wrong shape for reading. A feed says it in sentences —
 * "Dana moved RECON-2 to In Progress" — and keeps the values as the detail
 * rather than the headline.
 *
 * ── Why the verb comes from the field ───────────────────────────────────────
 *
 * "changed status from To Do to In Progress" is a database row read aloud.
 * Mapping the field to a verb costs one object and makes the feed scannable,
 * which is the only reason to have one.
 */
const PHRASING = {
  created: () => "created it",
  status: (from, to) => `moved it to ${to}`,
  assignee: (from, to) => (to ? `assigned it to ${to}` : "unassigned it"),
  priority: (from, to) => `set priority to ${to}`,
  due_date: (from, to) => (to ? `set the due date to ${to}` : "cleared the due date"),
  title: () => "renamed it",
  description: () => "rewrote the description",
}

export default function ActivityFeed({ items, loading, emptyText = "Nothing has happened yet." }) {
  if (loading) {
    return (
      <Stack spacing={1.5}>
        {[0, 1, 2].map((row) => (
          <Stack key={row} direction="row" spacing={1.5} alignItems="center">
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="text" sx={{ flex: 1 }} />
          </Stack>
        ))}
      </Stack>
    )
  }

  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    )
  }

  return (
    <Stack spacing={0}>
      {items.map((item, index) => {
        const phrase = PHRASING[item.field] || ((was, now) => `changed ${item.field}${now ? ` to ${now}` : ""}`)
        /* The resolved labels where the server produced them: an assignee is
           stored as a user id, and "assigned it to 36" is not a sentence. */
        const from = item.old_label ?? item.old_value
        const to = item.new_label ?? item.new_value
        return (
          <Stack key={item.id} direction="row" spacing={1.5} sx={{ position: "relative", pb: 2 }}>
            {/* A hairline joining the avatars, stopping at the last one — it is
                what turns a stack of rows into a sequence. */}
            {index < items.length - 1 ? (
              <Box
                sx={{
                  position: "absolute",
                  left: 13,
                  top: 30,
                  bottom: 0,
                  width: "2px",
                  bgcolor: "divider",
                  borderRadius: 1,
                }}
              />
            ) : null}

            <UserAvatar name={item.actor_name} avatarKey={item.actor_avatar} size={28} tooltip={false} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 650 }}>
                  {item.actor_name || "Somebody"}
                </Box>{" "}
                {phrase(from, to)}
                {item.ref_label ? (
                  <Chip size="small" label={item.ref_label} sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
                ) : null}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {relativeTime(item.created_at)}
              </Typography>
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}
